import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import { useTenantId } from '@/contexts/TenantContext';
import CycleLogDetail from '@/modules/payroll/components/payRuns/CycleLogDetail';
import CycleLogList from '@/modules/payroll/components/payRuns/CycleLogList';
import RunPayCycleModal from '@/modules/payroll/components/payRuns/RunPayCycleModal';
import PayrollDateRangePicker from '@/modules/payroll/components/shared/PayrollDateRangePicker';
import PayrollTableModal from '@/modules/payroll/components/shared/PayrollTableModal';
import {
  type CycleStatusFilter,
  type RunPayCycleOptions,
  usePayRunCycleLogs,
} from '@/modules/payroll/hooks/usePayRunCycleLogs';
import { parseCsvTable } from '@/modules/payroll/utils/csv.utils';
import {
  buildPayloadTable,
  CYCLE_STATUS_FILTERS,
  type IntegrationSyncSummary,
  type PayrollTableData,
} from '@/modules/payroll/utils/payRunCycle.utils';
import type { PayrollView } from '@/modules/payroll/views/payroll.view';

/**
 * Loads an integration's sync report (CSV) or sent payload (JSON) into a
 * table. Returns null when the file can't be fetched (e.g. storage CORS) or
 * parsed, so the caller can fall back to opening the raw file.
 */
const loadIntegrationFileTable = async (
  summary: IntegrationSyncSummary,
  file: 'report' | 'payload',
): Promise<PayrollTableData | null> => {
  const url = file === 'report' ? summary.reportUrl : summary.payloadUrl;
  if (!url) {
    return null;
  }
  const response = await fetch(url);
  if (!response.ok) {
    return null;
  }
  const text = await response.text();

  if (file === 'report') {
    const table = parseCsvTable(text);
    return table
      ? {
          title: `${summary.label} report`,
          subtitle: `Rows: ${table.rows.length}`,
          columns: table.headers,
          rows: table.rows,
        }
      : null;
  }

  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    return null;
  }
  const table = buildPayloadTable(payload);
  return table
    ? {
        title: `${summary.label} payload`,
        subtitle: `Timesheets: ${table.rows.length}`,
        ...table,
      }
    : null;
};

type PayrollPayRunsSectionProps = {
  view: PayrollView;
};

/** Pay run cycle history for the tenant, with cycle drill-down and run CTA. */
const PayrollPayRunsSection = ({ view }: PayrollPayRunsSectionProps) => {
  const tenantId = useTenantId();
  const payRuns = usePayRunCycleLogs(tenantId);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  const [openTable, setOpenTable] = useState<PayrollTableData | null>(null);

  const handleRunPayCycle = useCallback(
    async (options: RunPayCycleOptions) => {
      await payRuns.runPayCycle(options);
      const message = "Pay run queued. You'll get an email when it finishes.";
      setQueuedMessage(message);
      setIsRunModalOpen(false);
      toast.success(message);
    },
    [payRuns],
  );

  const handleForceSync = useCallback(
    async (cycleId: string) => {
      try {
        await payRuns.forceSyncCycle(cycleId);
        toast.success('Pay run cycle log sync was queued successfully.');
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : 'Unable to force sync this pay run cycle log.',
        );
      }
    },
    [payRuns],
  );

  const handleOpenIntegrationFile = useCallback(
    async (summary: IntegrationSyncSummary, file: 'report' | 'payload') => {
      const url = file === 'report' ? summary.reportUrl : summary.payloadUrl;
      try {
        const table = await loadIntegrationFileTable(summary, file);
        if (table) {
          setOpenTable(table);
          return;
        }
      } catch {
        // Fall through to opening the raw file.
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [],
  );

  const { activeCycle } = payRuns;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <PayrollDateRangePicker
          value={payRuns.period}
          onChange={payRuns.setPeriod}
          commitOnClose
        />
        <label className="flex flex-col gap-1 text-sm text-gray-700">
          Cycle status
          <select
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={payRuns.statusFilter}
            onChange={(event) =>
              payRuns.setStatusFilter(event.target.value as CycleStatusFilter)
            }
          >
            {CYCLE_STATUS_FILTERS.map((filter) => (
              <option key={filter.key} value={filter.key}>
                {filter.label}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="py-2 text-sm"
            onClick={() => void payRuns.refresh()}
            isLoading={payRuns.isLoading}
          >
            Sync pay runs
          </Button>
          {view.actions.showRunPayCycle && (
            <Button
              variant="primary"
              className="py-2 text-sm"
              onClick={() => setIsRunModalOpen(true)}
              disabled={!tenantId}
            >
              Run pay cycle
            </Button>
          )}
        </div>
        <div className="flex flex-col text-xs text-gray-500">
          {payRuns.lastSyncedAt && (
            <span>
              Synced{' '}
              {new Date(payRuns.lastSyncedAt).toLocaleTimeString('en-AU')}
            </span>
          )}
          {queuedMessage && <span>{queuedMessage}</span>}
        </div>
      </div>

      {payRuns.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {payRuns.error}
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="max-h-[70vh] overflow-y-auto lg:w-80 lg:shrink-0">
          <CycleLogList
            cycleLogs={payRuns.cycleLogs}
            selectedCycleId={payRuns.selectedCycleId}
            onSelect={payRuns.setSelectedCycleId}
            isLoading={payRuns.isLoading}
          />
        </div>
        <div className="min-w-0 flex-1">
          {activeCycle ? (
            <CycleLogDetail
              cycle={activeCycle}
              isLoadingDetails={payRuns.isLoadingDetails}
              detailsError={payRuns.detailsError}
              clientBillsById={payRuns.clientBillsById}
              billClientsById={payRuns.billClientsById}
              isLoadingBills={payRuns.isLoadingBills}
              isForceSyncing={payRuns.forceSyncingIds.has(activeCycle.id)}
              showForceSync={view.actions.showForceSyncCycle}
              onForceSync={() => void handleForceSync(activeCycle.id)}
              onOpenTable={setOpenTable}
              onOpenIntegrationFile={(summary, file) =>
                void handleOpenIntegrationFile(summary, file)
              }
            />
          ) : (
            !payRuns.isLoading &&
            payRuns.cycleLogs.length > 0 && (
              <p className="py-6 text-sm text-gray-500">
                Select a cycle to see its details.
              </p>
            )
          )}
        </div>
      </div>

      <RunPayCycleModal
        isOpen={isRunModalOpen}
        onClose={() => setIsRunModalOpen(false)}
        onSubmit={handleRunPayCycle}
      />
      <PayrollTableModal table={openTable} onClose={() => setOpenTable(null)} />
    </div>
  );
};

export default PayrollPayRunsSection;
